import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Loader2, Mail, Phone, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, boothUrl, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type Mode = "pick" | "qr" | "invite";

function InviteForm({ event, onDone }: { event: any; onDone: () => void }) {
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/vendor/capture/invite", { event_id: event.id, phone: phone.trim(), email: email.trim() });
      toast.success(phone.trim() && email.trim() ? "Invite texted and emailed" : phone.trim() ? "Invite texted" : "Invite emailed");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="capture-invite-form">
      <div className="space-y-1.5">
        <Label>Attendee's phone number</Label>
        <Input type="tel" inputMode="tel" autoFocus value={phone} placeholder="(555) 123-4567" onChange={(e) => setPhone(e.target.value)} className="h-12 text-lg" data-testid="capture-phone-input" />
      </div>
      <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" /></div>
      <div className="space-y-1.5">
        <Label>Attendee's email</Label>
        <Input type="email" inputMode="email" value={email} placeholder="name@example.com" onChange={(e) => setEmail(e.target.value)} className="h-12 text-lg" data-testid="capture-email-input" />
      </div>
      <p className="text-xs text-muted-foreground" data-testid="capture-invite-notice">We'll send one link to your booth by text and/or email, so the attendee can share their details and get your materials. The number and email are only used for this invite.</p>
      <Button onClick={send} disabled={busy || !(phone.trim() || email.trim())} className="h-12 w-full bg-primary text-white hover:bg-primary/90" data-testid="capture-send-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invite"}
      </Button>
    </div>
  );
}

const Choice = ({ icon: Icon, title, hint, onClick, testid }: any) => (
  <button onClick={onClick} className="flex w-full items-center gap-3 rounded-sm border p-4 text-left transition-colors hover:border-primary" data-testid={testid}>
    <Icon className="h-6 w-6 text-primary" /><span><span className="block font-semibold">{title}</span><span className="block text-xs text-muted-foreground">{hint}</span></span>
  </button>
);

export function CaptureLeadDialog({ onClose }: { onClose: () => void }) {
  const [events, setEvents] = useState<any[] | null>(null);
  const [event, setEvent] = useState<any>(null);
  const [mode, setMode] = useState<Mode>("pick");
  useEffect(() => {
    api.get("/vendor/events").then((r) => {
      const active = r.data.filter((e: any) => e.active);
      setEvents(active);
      if (active.length === 1) setEvent(active[0]);
    }).catch((e) => { toast.error(errMsg(e)); setEvents([]); });
  }, []);
  const back = () => (mode !== "pick" ? setMode("pick") : setEvent(null));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-md" data-testid="capture-lead-dialog">
        <DialogHeader><DialogTitle>Capture a lead{event ? ` \u00b7 ${event.name}` : ""}</DialogTitle></DialogHeader>
        {event && (events?.length || 0) + (mode !== "pick" ? 1 : 0) > 1 && (
          <button className="-mt-2 inline-flex w-fit items-center gap-1 text-xs text-muted-foreground" onClick={back} data-testid="capture-back-button"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        )}
        {events === null && <Loader2 className="mx-auto h-5 w-5 animate-spin" />}
        {events?.length === 0 && <p className="text-sm text-muted-foreground" data-testid="capture-no-events">You have no active events. Turn one on in Events &amp; booths first.</p>}
        {!event && events && events.length > 1 && (
          <div className="space-y-2" data-testid="capture-event-list">
            <p className="text-sm text-muted-foreground">Which event are you at?</p>
            {events.map((e) => (
              <button key={e.id} onClick={() => setEvent(e)} className="block w-full rounded-sm border p-3 text-left hover:border-primary" data-testid={`capture-event-${e.id}`}>
                <span className="block font-semibold">{e.name}</span><span className="block text-xs text-muted-foreground">{[e.venue, e.booth && `Booth ${e.booth}`].filter(Boolean).join(" \u00b7 ")}</span>
              </button>
            ))}
          </div>
        )}
        {event && mode === "pick" && (
          <div className="space-y-2">
            <Choice icon={QrCode} title="Show QR code" hint="The attendee scans it with their phone" onClick={() => setMode("qr")} testid="capture-choose-qr" />
            <Choice icon={Phone} title="Phone number or Email" hint="Send them a link to your booth" onClick={() => setMode("invite")} testid="capture-choose-phone" />
          </div>
        )}
        {event && mode === "qr" && (
          <div className="space-y-3 text-center" data-testid="capture-qr-view">
            <div className="mx-auto w-full max-w-[320px] rounded-sm border bg-white p-4"><QRCodeSVG value={boothUrl(event.slug)} size={512} level="M" className="h-auto w-full" /></div>
            <p className="text-sm text-muted-foreground">Ask the attendee to point their phone camera at the code.</p>
            <Button variant="secondary" onClick={onClose} data-testid="capture-done-button">Done</Button>
          </div>
        )}
        {event && mode === "invite" && <InviteForm event={event} onDone={() => setMode("pick")} />}
        {event && mode === "pick" && <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Mail className="h-3 w-3" /> Attendees always confirm their email before their details reach you.</p>}
      </DialogContent>
    </Dialog>
  );
}
